import { publicOrderRefsSchema } from "@erp/shared";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import {
  Download,
  ExternalLink,
  Printer,
  AlertTriangle,
  Archive,
  Ban,
  ChevronLeft,
  Flame,
  FileText,
  HandCoins,
  Landmark,
  LifeBuoy,
  MoreHorizontal,
  PackageCheck,
  Pencil,
  Receipt,
  Trash2,
  Truck,
  HandHeart,
} from "lucide-react";
import { useState, type ReactNode } from "react";
import { toast } from "sonner";
import { Badge } from "~/components/ui/badge";
import { Button, buttonVariants } from "~/components/ui/button";
import { Confirm } from "~/components/ui/confirm";
import { Menu, type MenuAction } from "~/components/ui/menu";
import { Page, PageHeader, Panel } from "~/components/ui/page";
import { Sheet } from "~/components/ui/sheet";
import { ErrorState, Skeleton } from "~/components/ui/states";
import { History } from "~/features/audit/history";
import { downloadLaposteFile } from "~/features/orders/laposte-export";
import { OrderLines } from "~/features/orders/order-lines";
import { PublicRefsFields, refsDraft, refsErrors, refsPayload, uploadOf, type RefsDraft } from "~/features/sales/public-refs";
import { DepositSheet } from "~/features/sales/deposit-fields";
import { companyQuery } from "~/features/sales/send-email";
import { INVOICE_TONE, ORDER_TONE, PAYMENT_TONE } from "~/features/sales/status";
import { api, unwrap } from "~/lib/api";
import { usePrint } from "~/lib/auto-print";
import { errorMessageKey } from "~/lib/errors";
import { useFormat, useT } from "~/lib/i18n";
import { useCan } from "~/lib/session";
import { HelpTip } from "~/components/ui/help-tip";
import { helpHint } from "~/features/help/hint";

export const Route = createFileRoute("/_app/orders/$id")({ component: OrderPage });

const fetchOrder = (id: string) => unwrap(api.orders({ id }).get());
type Order = Awaited<ReturnType<typeof fetchOrder>>;

function BackLink() {
  const t = useT();
  return (
    <Link to="/orders" className="mb-2 inline-flex items-center gap-1 text-sm text-fg-2 hover:text-fg">
      <ChevronLeft className="size-4" aria-hidden />
      {t("orders.title")}
    </Link>
  );
}

function OrderPage() {
  const { id } = Route.useParams();
  const query = useQuery({ queryKey: ["orders", id], queryFn: () => fetchOrder(id) });
  if (query.isPending)
    return (
      <Page>
        <BackLink />
        <Skeleton className="h-8 w-60" />
        <Skeleton className="mt-6 h-96 w-full" />
      </Page>
    );
  if (query.isError)
    return (
      <Page>
        <BackLink />
        <ErrorState error={query.error} onRetry={() => void query.refetch()} />
      </Page>
    );
  return <OrderView order={query.data} />;
}

function Section({ title, children, action }: { title: string; children: ReactNode; action?: ReactNode }) {
  return (
    <div className="border-b border-line px-5 py-5 last:border-b-0 md:px-6">
      <div className="mb-3 flex items-center justify-between gap-2">
        <h2 className="text-[15px] font-medium">{title}</h2>
        {action}
      </div>
      {children}
    </div>
  );
}

function OrderView({ order }: { order: Order }) {
  const t = useT();
  const format = useFormat();
  const can = useCan();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [cancelling, setCancelling] = useState(false);
  const [handing, setHanding] = useState(false);
  const [delivering, setDelivering] = useState(false);
  const [depositing, setDepositing] = useState(false);
  const company = useQuery(companyQuery);
  const open = order.status === "to_process" || order.status === "preparing";
  const toInvoice = order.lines.some((l) => l.toInvoice > 0);
  const toShip = order.lines.some((l) => l.toShip > 0);

  const printer = usePrint();
  const act = useMutation({
    mutationFn: async (kind: "prepare" | "delivered" | "hand" | "cancel" | "urgent" | "archive" | "invoice") => {
      const o = api.orders({ id: order.id });
      switch (kind) {
        case "prepare":
          return { kind, data: await unwrap(o.prepare.post()) } as const;
        case "delivered":
          return { kind, data: await unwrap(o.delivered.post()) } as const;
        case "hand":
          return { kind, data: await unwrap(o["hand-delivery"].post()) } as const;
        case "cancel":
          return { kind, data: await unwrap(o.cancel.post()) } as const;
        case "urgent":
          return { kind, data: await unwrap(o.urgent.post({ urgent: !order.urgent })) } as const;
        case "archive":
          return { kind, data: await unwrap(o.archive.post({ archived: !order.archivedAt })) } as const;
        case "invoice":
          return { kind, invoice: await unwrap(o.invoice.post()) } as const;
      }
    },
    onSuccess: (done) => {
      void queryClient.invalidateQueries({ queryKey: ["orders"] });
      void queryClient.invalidateQueries({ queryKey: ["stock"] });
      if (done.kind === "invoice") {
        void queryClient.invalidateQueries({ queryKey: ["invoices"] });
        toast.success(t("orders.invoiceCreated"));
        void navigate({ to: "/invoices/$id", params: { id: done.invoice.id } });
        return;
      }
      setCancelling(false);
      setDelivering(false);
      setHanding(false);
      void queryClient.invalidateQueries({ queryKey: ["shipments"] });
      toast.success(t("orders.updated"));
    },
    onError: (error) => toast.error(t(errorMessageKey(error))),
  });

  const primary: ReactNode = (() => {
    if (open && toShip && can("stock:write"))
      return (
        <>
          {order.status === "to_process" ? (
            <span className="inline-flex items-center gap-0.5">
              <Button
                onClick={() => {
                  // Opened in the click itself (not after the request) so the browser does not block it.
                  printer.print("order_delivery_note", order.id, `/api/orders/${order.id}/delivery-note`);
                  act.mutate("prepare");
                }}
                loading={act.isPending}
              >
                <PackageCheck />
                <span className="max-sm:sr-only">{t("orders.actions.prepare")}</span>
              </Button>
              <HelpTip id="order.prepare" />
            </span>
          ) : null}
          <Link to="/shipments/new" search={{ orderId: order.id }} className={buttonVariants({ variant: "primary" })}>
            <Truck />
            {t("orders.actions.ship")}
          </Link>
        </>
      );
    if (order.status === "shipped" && can("stock:write"))
      return (
        <span className="inline-flex items-center gap-0.5">
          <Button variant="primary" onClick={() => setDelivering(true)}>
            <PackageCheck />
            {t("orders.actions.delivered")}
          </Button>
          <HelpTip id="order.delivered" />
        </span>
      );
    return null;
  })();

  const menu: (MenuAction | "separator")[] = [];
  // A deposit is asked for before the final invoice, on orders the ERP invoices itself.
  const canDeposit =
    company.data?.depositInvoices &&
    order.source !== "shopify" &&
    order.status !== "cancelled" &&
    !order.invoices.some((i) => i.kind === "invoice" && i.status === "issued") &&
    can("invoices:issue");
  if (canDeposit)
    menu.push({
      label: t("orders.actions.depositInvoice"),
      hint: helpHint(t, "order.deposit"),
      icon: <HandCoins />,
      onSelect: () => setDepositing(true),
    });
  if (open && can("sales:write"))
    menu.push({
      label: order.urgent ? t("orders.actions.unmarkUrgent") : t("orders.actions.markUrgent"),
      hint: helpHint(t, "order.urgent"),
      icon: <Flame />,
      onSelect: () => act.mutate("urgent"),
    });
  // Handed over by the company itself (no carrier, no label): what remains leaves the stock, delivered.
  if (open && toShip && can("stock:write"))
    menu.push({
      label: t("orders.actions.handDelivery"),
      icon: <HandHeart />,
      onSelect: () => setHanding(true),
    });
  if (open && can("stock:write"))
    menu.push({
      label: t("shipments.laposte.action"),
      hint: helpHint(t, "shipment.laposteFile"),
      icon: <Truck />,
      onSelect: () => void downloadLaposteFile(t, [order.id]).catch((e: unknown) => toast.error(t(errorMessageKey(e)))),
    });
  if (order.status !== "cancelled" && can("sav:write"))
    menu.push({
      label: t("sav.actions.openFromOrder"),
      icon: <LifeBuoy />,
      onSelect: () => void navigate({ to: "/sav/new", search: { orderId: order.id } }),
    });
  if ((order.status === "delivered" || order.status === "cancelled") && can("sales:write"))
    menu.push({
      label: order.archivedAt ? t("orders.actions.unarchive") : t("orders.actions.archive"),
      hint: order.archivedAt ? undefined : helpHint(t, "order.archive"),
      icon: <Archive />,
      onSelect: () => act.mutate("archive"),
    });
  if (open && order.lines.every((l) => l.shippedQuantity === 0) && can("sales:write"))
    menu.push("separator", {
      label: t("orders.actions.cancel"),
      hint: helpHint(t, "order.cancel"),
      icon: <Ban />,
      tone: "danger",
      onSelect: () => setCancelling(true),
    });

  const address = (a: Order["party"]["billingAddress"]) =>
    a ? (
      format.address(a).map((line) => (
        <span key={line} className="block">
          {line}
        </span>
      ))
    ) : (
      <span className="text-fg-3">{t("common.states.none")}</span>
    );

  return (
    <Page className={/* room for the floating selection bar */ open && can("stock:write") ? "pb-28 md:pb-24" : undefined}>
      <PageHeader
        back={<BackLink />}
        title={<span className="tabular">{order.number}</span>}
        meta={
          <span className="flex flex-wrap items-center gap-2">
            <Badge tone={ORDER_TONE[order.status]}>{t(`orders.statuses.${order.status}`)}</Badge>
            <Badge tone={PAYMENT_TONE[order.paymentStatus]}>{t(`orders.payment.${order.paymentStatus}`)}</Badge>
            {order.urgent ? (
              <Badge tone="danger">
                <Flame className="size-3" aria-hidden />
                {t("orders.flags.urgent")}
              </Badge>
            ) : null}
            {open && !order.stockout ? <Badge tone="success">{t("orders.flags.ready")}</Badge> : null}
            {order.remainder ? <Badge tone="violet">{t("orders.flags.remainder")}</Badge> : null}
            <span>
              {t(`orders.sources.${order.source}`)} — {format.date(order.orderedAt, "datetime")}
            </span>
          </span>
        }
        actions={
          <>
            {order.status !== "cancelled" ? (
              <span className="inline-flex items-center gap-0.5">
                <a
                  href={`/api/orders/${order.id}/delivery-note`}
                  target="_blank"
                  rel="noreferrer"
                  className={buttonVariants({ variant: "secondary" })}
                  title={t("orders.actions.deliveryNote")}
                >
                  <Printer />
                  <span className="max-sm:sr-only">{t("orders.actions.deliveryNote")}</span>
                </a>
                <HelpTip id="order.deliveryNote" />
              </span>
            ) : null}
            {order.shopifyOrderUrl ? (
              <span className="inline-flex items-center gap-0.5">
                <a
                  href={order.shopifyOrderUrl}
                  target="_blank"
                  rel="noreferrer"
                  className={buttonVariants({ variant: "secondary" })}
                >
                  <ExternalLink />
                  <span className="max-sm:sr-only">{t("invoices.external.open")}</span>
                </a>
                <HelpTip id="shopify.openOrder" />
              </span>
            ) : null}
            {toInvoice && !order.invoicedInShopify && can("invoices:issue") && order.status !== "cancelled" ? (
              <span className="inline-flex items-center gap-0.5">
                <Button onClick={() => act.mutate("invoice")} loading={act.isPending}>
                  <Receipt />
                  {t("orders.actions.invoice")}
                </Button>
                <HelpTip id="order.invoice" />
              </span>
            ) : null}
            {primary}
            {menu.length ? (
              <Menu
                trigger={
                  <Button variant="ghost" size="icon" aria-label={t("common.actions.more")}>
                    <MoreHorizontal />
                  </Button>
                }
                actions={menu}
              />
            ) : null}
          </>
        }
      />

      {order.stockout && open ? (
        <p className="mb-5 flex items-center gap-2 rounded-panel bg-warning-soft px-4 py-3 text-sm text-warning">
          <AlertTriangle className="size-4 shrink-0" aria-hidden />
          {t("orders.stockoutHint")}
        </p>
      ) : null}

      {/* minmax(0, …) and min-w-0: the lines table scrolls inside its card instead of widening the page on a phone. */}
      <div className="grid grid-cols-[minmax(0,1fr)] gap-5 lg:grid-cols-[minmax(0,1fr)_320px]">
        <div className="flex min-w-0 flex-col gap-5">
          <Panel>
            <Section title={t("orders.sections.lines")}>
              <OrderLines orderId={order.id} lines={order.lines} editable={open && can("stock:write")} />
              <dl className="mt-4 ml-auto flex max-w-xs flex-col gap-1.5 text-sm">
                {order.totals.shippingCents ? (
                  <div className="flex justify-between">
                    <dt className="text-fg-2">{t("editor.totals.shipping")}</dt>
                    <dd className="tabular">{format.money(order.totals.shippingCents)}</dd>
                  </div>
                ) : null}
                <div className="flex justify-between">
                  <dt className="text-fg-2">{t("editor.totals.net")}</dt>
                  <dd className="tabular">{format.money(order.netCents)}</dd>
                </div>
                <div className="flex justify-between border-t border-line pt-1.5 font-medium">
                  <dt>{t("editor.totals.gross")}</dt>
                  <dd className="tabular">{format.money(order.grossCents)}</dd>
                </div>
              </dl>
            </Section>
            <Section title={t("orders.sections.shipments")}>
              {order.shipments.length === 0 ? (
                <p className="text-sm text-fg-3">{t("orders.noShipments")}</p>
              ) : (
                <ul className="-mx-2 flex flex-col">
                  {order.shipments.map((s) => (
                    <li key={s.id}>
                      <Link
                        to="/shipments/$id"
                        params={{ id: s.id }}
                        className="flex min-h-11 items-center justify-between gap-3 rounded-control px-2 text-sm hover:bg-surface-2"
                      >
                        <span className="tabular">{s.number}</span>
                        <span className="truncate text-fg-3 tabular">{s.trackingNumber ?? ""}</span>
                        <Badge>{t(`shipments.statuses.${s.status}`)}</Badge>
                      </Link>
                    </li>
                  ))}
                </ul>
              )}
            </Section>
            {/* Invoices are not the warehouse's business (guide: roles). */}
            {can("invoices:read") ? (
              <Section title={t("orders.sections.invoices")}>
                {order.invoices.length === 0 ? (
                  <p className="text-sm text-fg-3">
                    {t(order.invoicedInShopify ? "orders.invoicedInShopify" : "orders.noInvoices")}
                  </p>
                ) : (
                  <ul className="-mx-2 flex flex-col">
                    {order.invoices.map((i) => (
                      <li key={i.id} className="flex items-center gap-1">
                        <Link
                          to="/invoices/$id"
                          params={{ id: i.id }}
                          className="flex min-h-11 flex-1 items-center justify-between gap-3 rounded-control px-2 text-sm hover:bg-surface-2"
                        >
                          <span className="tabular">{i.number ?? t(`invoices.statuses.${i.status}`)}</span>
                          <span className="text-fg-2">
                            {t(`invoices.kinds.${i.kind}`)}
                            {i.issuer === "shopify" ? ` — ${t("invoices.issuers.shopify")}` : ""}
                          </span>
                          <span className="tabular">{format.money(i.grossCents)}</span>
                          <Badge tone={INVOICE_TONE[i.status]}>{t(`invoices.statuses.${i.status}`)}</Badge>
                        </Link>
                        {i.status === "issued" ? (
                          <a
                            href={`/api/invoices/${i.id}/pdf?download=1`}
                            download
                            className={buttonVariants({ variant: "ghost", size: "icon-sm" })}
                            aria-label={t("invoices.actions.download")}
                            title={t("invoices.actions.download")}
                          >
                            <Download />
                          </a>
                        ) : null}
                      </li>
                    ))}
                  </ul>
                )}
              </Section>
            ) : null}
            {order.savTickets.length ? (
              <Section title={t("orders.sections.sav")}>
                <ul className="-mx-2 flex flex-col">
                  {order.savTickets.map((s) => (
                    <li key={s.id}>
                      <Link
                        to="/sav/$id"
                        params={{ id: s.id }}
                        className="flex min-h-11 items-center justify-between gap-3 rounded-control px-2 text-sm hover:bg-surface-2"
                      >
                        <span className="tabular">{s.number}</span>
                        <span className="truncate">{s.subject}</span>
                        <Badge>{t(`sav.statuses.${s.status}`)}</Badge>
                      </Link>
                    </li>
                  ))}
                </ul>
              </Section>
            ) : null}
          </Panel>
        </div>

        <aside className="flex flex-col gap-5">
          <Panel>
            <Section title={t("orders.fields.customer")}>
              <p className="font-medium">
                {order.customerId ? (
                  <Link to="/customers/$id" params={{ id: order.customerId }} className="hover:underline">
                    {order.party.name}
                  </Link>
                ) : (
                  order.party.name
                )}
              </p>
              {order.party.email ? <p className="text-sm text-fg-2">{order.party.email}</p> : null}
              {order.party.phone ? <p className="text-sm text-fg-2 tabular">{order.party.phone}</p> : null}
              {order.externalReference ? (
                <p className="mt-2 text-sm">
                  <span className="text-fg-3">{t("orders.fields.reference")}</span> {order.externalReference}
                </p>
              ) : null}
              {order.quoteId ? (
                <Link
                  to="/quotes/$id"
                  params={{ id: order.quoteId }}
                  className="mt-2 inline-block text-sm text-fg-2 underline-offset-4 hover:underline"
                >
                  {t("orders.fromQuote")}
                </Link>
              ) : null}
            </Section>
            <Section title={t("customers.fields.shippingAddress")}>
              <address className="text-sm not-italic leading-relaxed">
                {address(order.party.shippingAddress ?? order.party.billingAddress)}
              </address>
            </Section>
          </Panel>
          {order.subject || order.deliveryTime || order.terms ? (
            <Panel>
              <Section title={t("orders.fromQuote")}>
                <dl className="flex flex-col gap-2 text-sm">
                  {(
                    [
                      [t("quotes.fields.subject"), order.subject],
                      [t("quotes.fields.deliveryTime"), order.deliveryTime],
                      [t("editor.terms"), order.terms],
                    ] as const
                  ).map(([label, value]) =>
                    value ? (
                      <div key={label}>
                        <dt className="text-fg-2">{label}</dt>
                        <dd className="whitespace-pre-line">{value}</dd>
                      </div>
                    ) : null,
                  )}
                </dl>
              </Section>
            </Panel>
          ) : null}
          {order.customerKind === "public" || order.engagementNumber || order.purchaseOrderNumber || order.purchaseOrder ? (
            <PublicRefsPanel order={order} />
          ) : null}
          <Panel>
            <Section title={t("orders.sections.activity")}>
              <History entityType="order" entityId={order.id} />
            </Section>
          </Panel>
        </aside>
      </div>

      <Confirm
        open={delivering}
        onOpenChange={setDelivering}
        title={t("confirms.orderDelivered.title")}
        body={t("confirms.orderDelivered.body")}
        confirmLabel={t("confirms.orderDelivered.confirm")}
        loading={act.isPending}
        onConfirm={() => act.mutate("delivered")}
      />
      <Confirm
        open={handing}
        onOpenChange={setHanding}
        title={t("confirms.handDelivery.title")}
        body={t("confirms.handDelivery.body")}
        confirmLabel={t("confirms.handDelivery.confirm")}
        loading={act.isPending}
        onConfirm={() => act.mutate("hand")}
      />
      <Confirm
        open={cancelling}
        onOpenChange={setCancelling}
        title={t("orders.cancelTitle")}
        body={t("orders.cancelBody")}
        confirmLabel={t("orders.actions.cancel")}
        tone="danger"
        loading={act.isPending}
        onConfirm={() => act.mutate("cancel")}
      />
      {canDeposit ? (
        <DepositSheet
          orderId={order.id}
          grossCents={order.totals.grossCents}
          defaultBp={company.data?.depositDefaultBp ?? 3000}
          open={depositing}
          onOpenChange={setDepositing}
        />
      ) : null}
    </Page>
  );
}

/** Commitment number, SIRET, service and purchase order of a public body: editable until an invoice is issued. */
function PublicRefsPanel({ order }: { order: Order }) {
  const t = useT();
  const format = useFormat();
  const can = useCan();
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<RefsDraft>(() => refsDraft(order, order.party));
  const [submitted, setSubmitted] = useState(false);
  // Same rule as the server: an issued invoice or deposit invoice freezes the references.
  const locked = order.invoices.some((i) => (i.kind === "invoice" || i.kind === "deposit") && i.status === "issued");
  const editable = !locked && can("sales:write");
  const o = api.orders({ id: order.id });

  const done = (next: Order) => {
    queryClient.setQueryData(["orders", order.id], next);
    void queryClient.invalidateQueries({ queryKey: ["orders"] });
    void queryClient.invalidateQueries({ queryKey: ["invoices"] });
  };
  const save = useMutation({
    mutationFn: async () => {
      let next = await unwrap(o["public-refs"].put(publicOrderRefsSchema.parse(refsPayload(draft))));
      if (draft.file) next = await unwrap(o["purchase-order"].put(await uploadOf(draft.file)));
      if (draft.deliveryNote) next = await unwrap(o["signed-delivery-note"].put(await uploadOf(draft.deliveryNote)));
      return next;
    },
    onSuccess: (next) => {
      done(next);
      setEditing(false);
      toast.success(t("orders.refs.saved"));
    },
    onError: (error) => toast.error(t(errorMessageKey(error))),
  });
  const [removing, setRemoving] = useState<{ kind: "purchase-order" | "signed-delivery-note"; name: string } | null>(null);
  const removeFile = useMutation({
    mutationFn: (kind: "purchase-order" | "signed-delivery-note") => unwrap(o[kind].delete()),
    onSuccess: (next) => {
      done(next);
      setRemoving(null);
      toast.success(t("orders.refs.saved"));
    },
    onError: (error) => toast.error(t(errorMessageKey(error))),
  });
  const open = () => {
    setDraft(refsDraft(order, order.party));
    setSubmitted(false);
    setEditing(true);
  };
  const submit = () => {
    setSubmitted(true);
    if (Object.keys(refsErrors(draft)).length === 0) save.mutate();
  };

  const rows: [string, string | null][] = [
    [t("orders.refs.engagementNumber"), order.engagementNumber],
    [t("orders.refs.purchaseOrderNumber"), order.purchaseOrderNumber],
    [t("orders.refs.billingSiret"), order.billingSiret ?? order.party.siret],
    [t("orders.refs.chorusServiceCode"), order.chorusServiceCode ?? order.party.chorusServiceCode],
  ];
  const empty = !order.engagementNumber && !order.purchaseOrderNumber && !order.purchaseOrder && !order.signedDeliveryNote;
  const files = [
    { kind: "purchase-order" as const, file: order.purchaseOrder, label: t("orders.refs.purchaseOrder") },
    { kind: "signed-delivery-note" as const, file: order.signedDeliveryNote, label: t("orders.refs.signedDeliveryNote") },
  ];

  return (
    <Panel>
      <Section
        title={t("orders.refs.title")}
        action={
          <span className="inline-flex items-center gap-0.5">
            {editable ? (
              <Button variant="ghost" size="icon-sm" aria-label={t("orders.refs.edit")} onClick={open}>
                <Pencil />
              </Button>
            ) : null}
            <HelpTip id="order.publicRefs" />
          </span>
        }
      >
        {empty ? (
          <p className="mb-3 flex items-start gap-2 text-sm text-fg-3">
            <Landmark className="mt-0.5 size-4 shrink-0" aria-hidden />
            {t("orders.refs.missing")}
          </p>
        ) : null}
        <dl className="flex flex-col gap-1.5 text-sm">
          {rows.map(([label, value]) => (
            <div key={label} className="flex justify-between gap-3">
              <dt className="text-fg-2">{label}</dt>
              <dd className="truncate text-right tabular">{value ?? "—"}</dd>
            </div>
          ))}
        </dl>
        {files.map(({ kind, file, label }) =>
          file ? (
            <div key={kind} className="mt-3 flex items-center justify-between gap-2">
              <a
                href={`/api/orders/${order.id}/${kind}`}
                target="_blank"
                rel="noreferrer"
                title={label}
                className="flex min-h-11 min-w-0 items-center gap-2 text-sm font-medium underline-offset-4 hover:underline"
              >
                <FileText className="size-4 shrink-0 text-fg-3" aria-hidden />
                <span className="min-w-0 truncate">
                  <span className="text-fg-3 font-normal">{label} · </span>
                  {file.name}
                </span>
                <span className="shrink-0 font-normal text-fg-3 tabular">
                  {format.number(Math.max(1, Math.round(file.size / 1024)), { style: "unit", unit: "kilobyte" })}
                </span>
              </a>
              {editable ? (
                <Button
                  variant="ghost"
                  size="icon-sm"
                  aria-label={t("orders.refs.removeFile")}
                  onClick={() => setRemoving({ kind, name: file.name })}
                >
                  <Trash2 />
                </Button>
              ) : null}
            </div>
          ) : null,
        )}
        {locked ? <p className="mt-3 text-xs text-fg-3">{t("orders.refs.locked")}</p> : null}
      </Section>
      <Sheet
        open={editing}
        onOpenChange={setEditing}
        title={t("orders.refs.title")}
        footer={
          <>
            <Button onClick={() => setEditing(false)}>{t("common.actions.cancel")}</Button>
            <Button variant="primary" loading={save.isPending} onClick={submit}>
              {t("common.actions.save")}
            </Button>
          </>
        }
      >
        <PublicRefsFields
          value={draft}
          onChange={setDraft}
          showErrors={submitted}
          currentFile={order.purchaseOrder?.name ?? null}
          currentDeliveryNote={order.signedDeliveryNote?.name ?? null}
        />
      </Sheet>
      <Confirm
        open={removing !== null}
        onOpenChange={(open) => (open ? null : setRemoving(null))}
        title={t("confirms.orderFileRemove.title")}
        body={t("confirms.orderFileRemove.body", { name: removing?.name ?? "" })}
        confirmLabel={t("confirms.orderFileRemove.confirm")}
        tone="danger"
        loading={removeFile.isPending}
        onConfirm={() => removing && removeFile.mutate(removing.kind)}
      />
    </Panel>
  );
}
